import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
// Privacy/Terms/Contact moved to standalone HTML pages in public/
import { logEvent } from './analytics';

const TypingSpeedTest = () => {
  // Move commonWords inside useMemo to prevent dependency issues
  const commonWords = useMemo(() => [
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'it', 'for', 'not', 'on', 'with', 'as', 'you', 'do', 'at',
    'this', 'but', 'his', 'by', 'from', 'they', 'she', 'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out',
    'if', 'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year',
    'your', 'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use',
    'two', 'how', 'our', 'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because', 'any', 'these', 'give', 'day', 'most', 'us', 'water', 'long', 'very',
    'find', 'right', 'may', 'still', 'should', 'say', 'each', 'where', 'those', 'much', 'family', 'own', 'leave', 'put', 'old', 'while', 'mean', 'keep', 'student', 'why',
    'let', 'great', 'same', 'big', 'group', 'begin', 'seem', 'country', 'help', 'talk', 'turn', 'ask', 'made', 'social', 'large', 'until', 'here', 'follow', 'around', 'parent',
    'only', 'stop', 'computer', 'head', 'example', 'page', 'together', 'business', 'point', 'part', 'during', 'against', 'place', 'number', 'public', 'read', 'problem', 'fact', 'hand', 'high',
    'right', 'small', 'another', 'woman', 'world', 'life', 'different', 'member', 'state', 'while', 'area', 'might', 'system', 'program', 'question', 'government', 'company', 'number', 'group', 'problem',
    'never', 'include', 'service', 'however', 'although', 'something', 'house', 'school', 'important', 'often', 'provide', 'development', 'power', 'money', 'community', 'within', 'support', 'effective', 'rather', 'organization',
    'working', 'structure', 'individual', 'training', 'activity', 'medical', 'building', 'politics', 'result', 'available', 'save', 'consumer', 'discuss', 'career', 'growth', 'analysis', 'popular', 'traditional', 'generation', 'senior'
  ], []);

  // Generate random words with better distribution
  const generateWords = useCallback((count = 30) => {
    const words = [];
    const usedWords = new Set();

    while (words.length < count) {
      const randomWord = commonWords[Math.floor(Math.random() * commonWords.length)];
      // Add some variety by allowing word reuse after a gap
      if (!usedWords.has(randomWord) || words.length > 10) {
        words.push(randomWord);
        usedWords.add(randomWord);

        // Clear used words occasionally for variety
        if (usedWords.size > 20) {
          usedWords.clear();
        }
      }
    }
    return words.join(' ');
  }, [commonWords]);

  // Generate initial text that fills the typing area
  const generateInitialText = useCallback(() => {
    // Generate enough words to fill approximately 4-5 lines of text
    const initialWordCount = 64;
    return generateWords(initialWordCount);
  }, [generateWords]);

  // State management
  const [currentText, setCurrentText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [testDuration, setTestDuration] = useState(30);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isActive, setIsActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [overlayHidden, setOverlayHidden] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_challengeError, setChallengeError] = useState(null);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeMode, setChallengeMode] = useState(false);
  const [challengeOutcome, setChallengeOutcome] = useState(null); // 'won' | 'lost' | null
  // sharedChallengeId is now represented in the static DOM; no React-only state needed
  const [creatorNameInput, setCreatorNameInput] = useState('Unknown');
  // eslint-disable-next-line no-unused-vars
  const [_creatingChallenge, setCreatingChallenge] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_createError, setCreateError] = useState(null);
  const [textVersion, setTextVersion] = useState(0); // Force re-render when text changes
  const currentPage = 'home';

  // Refs
  const inputRef = useRef(null);
  const intervalRef = useRef(null);
  const typingAreaRef = useRef(null);
  const innerRef = useRef(null);
  const resultsRef = useRef(null);
  const lastGenerationPosition = useRef(0);
  const VISIBLE_LINES = 4;
  const LINE_HEIGHT = 48; // px — controls the visible line height and container size (increased for more space)

  const [hasStaticDOM, setHasStaticDOM] = useState(false);

  // Keep a ref copy of challengeData so window-exposed handlers always read latest
  const challengeDataRef = useRef(null);

  const escapeHtml = (unsafe) => String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  useEffect(() => {
    try {
      const shell = document.getElementById('app-shell');
      const isChallengeRoute = /^\/challenge\/[^/]+$/.test(window.location.pathname || '');
      if (shell) {
        shell.style.display = isChallengeRoute ? 'none' : '';
      }
      setHasStaticDOM(Boolean(shell) && !isChallengeRoute);
    } catch (e) {
      setHasStaticDOM(false);
    }
  }, []);

  // Keep app-shell visibility in sync when challengeMode or challenge modal state changes.
  useEffect(() => {
    try {
      const shell = document.getElementById('app-shell');
      const isChallengeRoute = /^\/challenge\/[^/]+$/.test(window.location.pathname || '');
      if (!shell) return;
      // If the app is running in challengeMode (user accepted) or the challenge modal is open,
      // ensure the shell stays visible so users can interact with controls.
      if (challengeMode || showChallengeModal) {
        shell.style.display = '';
        setHasStaticDOM(Boolean(shell));
        return;
      }
      // Otherwise, hide shell only when the path is a challenge route.
      shell.style.display = isChallengeRoute ? 'none' : '';
      setHasStaticDOM(Boolean(shell) && !isChallengeRoute);
    } catch (e) { /* noop */ }
  }, [challengeMode, showChallengeModal]);

  // Initialize text on component mount
  useEffect(() => {
    const initialText = generateInitialText();
    setCurrentText(initialText);
    // start generation guard at 0 (no words typed yet)
    lastGenerationPosition.current = 0;
  }, [generateInitialText]);

  // Focus input on load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Ensure body class follows `darkMode` for non-static and static paths
  useEffect(() => {
    try {
      document.body.classList.toggle('td-dark', darkMode);
      // If switching back to light, remove common dark utility classes that may have been added
      if (!darkMode) {
        const containers = [document.getElementById('app-shell'), document.getElementById('extra-content'), document.getElementById('site-footer')];
        containers.forEach(c => {
          if (!c) return;
          c.querySelectorAll('.bg-gray-800, .bg-gray-900, .bg-gray-700, .text-gray-300, .border-gray-700').forEach(el => {
            el.classList.remove('bg-gray-800', 'bg-gray-900', 'bg-gray-700', 'text-gray-300', 'border-gray-700');
          });
        });
      }
    } catch (e) { /* noop */ }
  }, [darkMode]);



  // Handle /challenge/:id route — fetch challenge from Firestore and show modal
  useEffect(() => {
    const path = window.location.pathname || '';
    const match = path.match(/^\/challenge\/([^/]+)$/);
    if (!match) return;

    const shell = document.getElementById('app-shell');
    shell.style.display = '';
    setHasStaticDOM(Boolean(shell));

    const routeChallengeId = match[1];
    const fetchChallenge = async () => {
      setChallengeLoading(true);
      setChallengeError(null);
      try {
        const app = getApp();
        const db = getFirestore(app);
        const ref = doc(db, 'challenges', routeChallengeId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setChallengeError('Challenge not found');
          setChallengeData(null);
          setShowChallengeModal(false);
        } else {
          setChallengeData(snap.data());
          setShowChallengeModal(true);
        }
      } catch (err) {
        setChallengeError(String(err));
        setChallengeData(null);
        setShowChallengeModal(false);
      } finally {
        setChallengeLoading(false);
      }
    };

    fetchChallenge();
  }, []);

  // Helper to format Firestore Timestamp or plain string
  const formatTimestamp = (val) => {
    if (!val) return '';
    // Firestore Timestamp has toDate()
    if (typeof val === 'object' && typeof val.toDate === 'function') {
      try {
        return val.toDate().toLocaleString();
      } catch (e) {
        return String(val);
      }
    }
    // If it's an object with seconds/nanoseconds (older SDK), convert
    if (val && typeof val === 'object' && 'seconds' in val) {
      try {
        const date = new Date(val.seconds * 1000 + Math.floor((val.nanoseconds || 0) / 1000000));
        return date.toLocaleString();
      } catch (e) {
        return String(val);
      }
    }
    return String(val);
  };

  // Smart text generation - add more content when user approaches the end
  const checkAndGenerateMoreText = useCallback(() => {
    const typedChars = userInput.length;
    const totalChars = currentText.length;

    if (totalChars === 0) return;

    const ratio = typedChars / totalChars;

    // When user has typed 80% or more of the available characters, append more.
    // Use lastGenerationPosition to avoid rapid re-generation (require at least 50 new chars typed since last generation).
    if (ratio >= 0.8 && typedChars > lastGenerationPosition.current + 50) {
      const additionalText = ' ' + generateWords(40);
      setCurrentText(prev => prev + additionalText);
      lastGenerationPosition.current = typedChars;
      setTextVersion(prev => prev + 1);
    }
  }, [userInput, currentText, generateWords]);

  // Manage view like a typewriter: show exactly VISIBLE_LINES (1 above, current, 2 below)
  const updateView = useCallback((smooth = false) => {
    if (!typingAreaRef.current || !innerRef.current) return;

    const inner = innerRef.current;

    const cursorEl = inner.querySelector('[data-cursor="true"]');
    let topLine = 0;

    if (cursorEl) {
      // compute cursor line relative to inner
      const cursorOffsetTop = cursorEl.offsetTop;
      const cursorLine = Math.floor(cursorOffsetTop / LINE_HEIGHT);
      topLine = Math.max(0, cursorLine - 1); // show one line above current
    }

    // clamp to available content
    const totalLines = Math.max(0, Math.ceil(inner.scrollHeight / LINE_HEIGHT));
    const maxTopLine = Math.max(0, totalLines - VISIBLE_LINES);
    topLine = Math.min(topLine, maxTopLine);

    const translateY = -topLine * LINE_HEIGHT;
    inner.style.transition = smooth ? 'transform 300ms ease' : 'none';
    inner.style.transform = `translateY(${translateY}px)`;
  }, []);

  // Calculate metrics
  const calculateMetrics = useCallback(() => {
    const totalChars = userInput.length;
    const correctChars = userInput.split('').reduce((acc, char, index) => {
      return acc + (char === currentText[index] ? 1 : 0);
    }, 0);

    const timeElapsed = testDuration - timeLeft;
    const minutes = timeElapsed / 60;
    const seconds = timeElapsed;

    const wpm = minutes > 0 ? Math.round((correctChars / 5) / minutes) : 0;
    const cpm = minutes > 0 ? Math.round(correctChars / minutes) : 0;
    const cps = seconds > 0 ? (correctChars / seconds).toFixed(1) : '0.0';
    // When no characters typed yet, show 0% accuracy instead of 100%
    const accuracy = totalChars > 0 ? Math.round((correctChars / totalChars) * 100) : 0;
    const errors = totalChars - correctChars;

    return { wpm, cpm, cps, accuracy, errors, correctChars, totalChars };
  }, [userInput, currentText, testDuration, timeLeft]);

  // Current metrics snapshot
  const metrics = calculateMetrics();

  // Ensure view aligns with initial text on load or when text changes
  useEffect(() => {
    // small timeout to allow DOM render
    setTimeout(() => updateView(false), 20);
  }, [currentText, updateView]);

  // Start test automatically on first keystroke
  const startTest = useCallback(() => {
    if (!isActive && !isCompleted) {
      setIsActive(true);
      setTimeLeft(testDuration);
    }
  }, [isActive, isCompleted, testDuration]);

  // Log start event
  useEffect(() => {
    if (isActive) {
      logEvent('typing_test_started', { test_mode: `${testDuration}s` });
    }
  }, [isActive, testDuration]);

  // Reset test
  const resetTest = useCallback(() => {
    setIsActive(false);
    setIsCompleted(false);
    setUserInput('');
    setTimeLeft(testDuration);

    // Generate fresh text
    const newText = generateInitialText();
    setCurrentText(newText);
    // reset generation guard so new typing can trigger generation
    lastGenerationPosition.current = 0;
    setTextVersion(prev => prev + 1);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Reset scroll position
    if (typingAreaRef.current && innerRef.current) {
      innerRef.current.style.transition = 'none';
      innerRef.current.style.transform = 'translateY(0px)';
    }

    // focus after a tick to ensure DOM updated, and realign view
    setTimeout(() => {
      inputRef.current?.focus();
      updateView(false);
    }, 20);
    // show overlay again after reset
    setOverlayHidden(false);
    // Clear static results and related UI when present
    try {
      const results = document.getElementById('results');
      if (results) {
        results.classList.add('hidden');
        try { results.style.removeProperty('display'); } catch (e) { /* noop */ }
        try { results.style.setProperty('display', 'none', 'important'); } catch (e) { /* noop */ }
      }

      // Reset top-level metric displays
      const setText = (id, text) => { try { const el = document.getElementById(id); if (el) el.textContent = text; } catch (e) { /* noop */ } };
      setText('metric-wpm', '0');
      setText('metric-cpm', '0');
      setText('metric-cps', '0.0');
      setText('metric-accuracy', '0%');
      setText('metric-errors', '0');
      setText('metric-chars', '0');

      setText('results-wpm', '0');
      setText('results-accuracy', '0%');
      setText('results-cps', '0.0');
      setText('results-total', '0');
      setText('results-correct', '0');
      setText('results-errors', '0');
      setText('results-cpm', '0');

      // Progress UI
      try { const pf = document.getElementById('progress-fill'); if (pf) pf.style.width = '0%'; } catch (e) { /* noop */ }
      try { const pc = document.getElementById('progress-chars'); if (pc) pc.textContent = 'Characters typed: 0'; } catch (e) { /* noop */ }
      try { const pa = document.getElementById('progress-accuracy'); if (pa) pa.textContent = 'Accuracy: 0%'; } catch (e) { /* noop */ }

      // Hide challenge-specific sections
      try { const chHeader = document.getElementById('results-challenge-header'); if (chHeader) chHeader.classList.add('hidden'); } catch (e) { /* noop */ }
      try { const regHeader = document.getElementById('results-header'); if (regHeader) regHeader.classList.remove('hidden'); } catch (e) { /* noop */ }
      try { const cs = document.getElementById('results-challenge-summary'); if (cs) cs.classList.add('hidden'); } catch (e) { /* noop */ }

      // Hide exit buttons
      try { const headerExit = document.getElementById('header-exit-challenge'); if (headerExit) headerExit.classList.add('hidden'); } catch (e) { /* noop */ }
      try { const resultsExit = document.getElementById('results-exit-challenge'); if (resultsExit) resultsExit.classList.add('hidden'); } catch (e) { /* noop */ }

      try { window._lastResults = null; } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }
  }, [testDuration, generateInitialText, updateView]);

  const exitChallenge = useCallback(() => {
    // go back to home and clear challenge state
    try { window.history.pushState({}, '', '/'); } catch (e) { /* noop */ }
    setChallengeMode(false);
    setChallengeData(null);
    setChallengeOutcome(null);
    setShowChallengeModal(false);
    // restore default duration and reset test
    setTestDuration(30);
    setTimeLeft(30);
    setTimeout(() => resetTest(), 10);
  }, [resetTest]);

  // Timer effect
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsActive(false);
            setIsCompleted(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeLeft]);

  // Scroll to results when test completes
  useEffect(() => {
    if (isCompleted) {
      // give React a tick to render the results section
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      // Log completion with metrics
      const typingDuration = testDuration - timeLeft; // seconds
      const metricsNow = calculateMetrics();
      logEvent('typing_test_completed', {
        test_mode: `${testDuration}s`,
        typing_duration: typingDuration,
        wpm_achieved: metricsNow.wpm,
        accuracy: metricsNow.accuracy,
      });

      // If we're in challenge mode, determine outcome
      if (challengeMode && challengeData) {
        const creatorWpm = Number(challengeData.creatorWpm || 0);
        const creatorAccuracy = Number(challengeData.creatorAccuracy || 0);
        const won = metricsNow.wpm >= creatorWpm && metricsNow.accuracy >= creatorAccuracy;
        setChallengeOutcome(won ? 'won' : 'lost');
        // Notify static shell immediately with computed outcome
        try {
          try { window._lastResults = { metrics: metricsNow, challengeMode: challengeMode, challengeData: challengeData, challengeOutcome: (won ? 'won' : 'lost') }; } catch (e) { /* noop */ }
          if (typeof window.showResults === 'function') {
            try { window.showResults({ metrics: metricsNow, challengeMode: challengeMode, challengeData: challengeData, challengeOutcome: (won ? 'won' : 'lost') }); } catch (e) { /* noop */ }
          }
        } catch (e) { /* noop */ }
      } else {
        setChallengeOutcome(null);
        try {
          try { window._lastResults = { metrics: metricsNow, challengeMode: challengeMode, challengeData: challengeData, challengeOutcome: null }; } catch (e) { /* noop */ }
          if (typeof window.showResults === 'function') {
            try { window.showResults({ metrics: metricsNow, challengeMode: challengeMode, challengeData: challengeData, challengeOutcome: null }); } catch (e) { /* noop */ }
          }
        } catch (e) { /* noop */ }
      }
    }
  }, [isCompleted, calculateMetrics, timeLeft, testDuration, challengeMode, challengeData]);

  // Generate an 8-character alphanumeric challenge id
  const generateChallengeId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  // Create a new challenge in Firestore and open share modal
  const createAndShareChallenge = useCallback(async (nameParam) => {
    try { const sel = document.getElementById('share-error'); if (sel) { sel.style.display = 'none'; sel.textContent = ''; } } catch (e) { /* noop */ }
    setCreateError(null);
    // validate name
    const name = (nameParam || creatorNameInput || '').trim();
    if (nameParam) setCreatorNameInput(name);
    if (!name || name.toLowerCase() === 'unknown') {
      setCreateError('Please enter a valid creator name before sharing.');
      return;
    }
    setCreatingChallenge(true);
    try {
      const app = getApp();
      const db = getFirestore(app);
      // ensure unique id by trying a few times
      let id = generateChallengeId();
      let attempts = 0;
      while (attempts < 5) {
        const ref = doc(db, 'challenges', id);
        const snap = await getDoc(ref);
        if (!snap.exists()) break;
        id = generateChallengeId();
        attempts++;
      }

      const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days

      const data = {
        creatorName: name || (creatorNameInput && creatorNameInput.trim()) || 'Unknown',
        creatorWpm: Math.round(metrics.wpm),
        creatorAccuracy: Number(metrics.accuracy),
        duration: Number(testDuration),
        createdAt: serverTimestamp(),
        expiresAt: expires,
      };

      await setDoc(doc(db, 'challenges', id), data);

      // close static create modal if present
      try {
        const cm = document.getElementById('create-challenge-modal');
        if (cm) cm.classList.add('hidden');
      } catch (e) { /* noop */ }

      // Populate and open the static share modal when the static DOM is present
      try {
        const shareModal = document.getElementById('share-modal');
        const shareLinkEl = document.getElementById('share-link');
        const shareIdEl = document.getElementById('share-id');
        const shareCreatorEl = document.getElementById('share-creator');
        const shareWpmEl = document.getElementById('share-wpm');
        const shareAccEl = document.getElementById('share-accuracy');
        const shareDurEl = document.getElementById('share-duration');
        const link = `${window.location.origin}/challenge/${id}`;
        if (shareLinkEl) shareLinkEl.textContent = link;
        if (shareIdEl) shareIdEl.textContent = id;
        if (shareCreatorEl) shareCreatorEl.textContent = data.creatorName || name || 'Unknown';
        if (shareWpmEl) shareWpmEl.textContent = String(Math.round(data.creatorWpm || 0));
        if (shareAccEl) shareAccEl.textContent = `${data.creatorAccuracy || 0}%`;
        if (shareDurEl) shareDurEl.textContent = `${data.duration || testDuration}s`;
        // expose last created id for static polling
        try { window._lastSharedChallengeId = id; } catch (e) { /* noop */ }
        if (shareModal) shareModal.classList.remove('hidden');
      } catch (e) { /* noop */ }
    } catch (err) {
      try {
        const el = document.getElementById('share-error'); if (el) { el.style.display = 'block'; el.textContent = String(err); }
      } catch (e) { /* noop */ }
    } finally {
      setCreatingChallenge(false);
    }
  }, [creatorNameInput, metrics, testDuration]);

  // Copy handled by static share modal; React copy handler removed.



  // Handle input change
  const handleInputChange = useCallback((e) => {
    // Ignore input when test is completed
    if (isCompleted) return;
    const value = e.target.value;

    // Start test on first character
    if (value.length === 1 && userInput.length === 0) {
      startTest();
    }

    // Prevent typing beyond current text length
    if (value.length <= currentText.length) {
      setUserInput(value);
    }
  }, [currentText.length, startTest, userInput.length, isCompleted]);

  // Check for more text generation and update typewriter view after user input changes
  useEffect(() => {
    if (isActive) {
      checkAndGenerateMoreText();
      updateView(false);
    }
  }, [userInput, isActive, checkAndGenerateMoreText, updateView]);

  // Update view when more text is appended; smooth transition
  useEffect(() => {
    if (isActive) {
      // allow DOM to update then smoothly translate inner content
      setTimeout(() => updateView(true), 30);
    }
  }, [textVersion, isActive, updateView]);

  // Handle key press
  const handleKeyPress = useCallback((e) => {
    // Prevent spaces at the beginning
    if (e.key === ' ' && userInput.length === 0) {
      e.preventDefault();
    }
  }, [userInput.length]);



  // Helpers to open/close the static Create Challenge modal and expose create handler to static DOM
  const openCreateModal = useCallback(() => {
    try {
      const modal = document.getElementById('create-challenge-modal');
      if (!modal) return;
      const nameInputEl = document.getElementById('create-creator-name');
      if (nameInputEl) nameInputEl.value = (creatorNameInput && creatorNameInput.trim()) || 'Unknown';
      // Populate metrics from static DOM (avoid referencing metrics in deps)
      const w = document.getElementById('create-metric-wpm');
      const staticW = document.getElementById('metric-wpm');
      if (w) w.textContent = staticW ? (staticW.textContent || '0') : '0';
      const a = document.getElementById('create-metric-accuracy');
      const staticA = document.getElementById('metric-accuracy');
      if (a) a.textContent = staticA ? (staticA.textContent || '0%') : '0%';
      const d = document.getElementById('create-metric-duration'); if (d) d.textContent = `${testDuration}s`;
      const err = document.getElementById('create-error'); if (err) err.textContent = '';
      modal.classList.remove('hidden');
      if (nameInputEl) nameInputEl.focus();
    } catch (e) { /* noop */ }
  }, [creatorNameInput, testDuration]);

  const closeCreateModal = useCallback(() => {
    try { const modal = document.getElementById('create-challenge-modal'); if (modal) modal.classList.add('hidden'); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    challengeDataRef.current = challengeData;
  }, [challengeData]);
  useEffect(() => {
    try {
      window.openCreateModal = openCreateModal;
      window.closeCreateModal = closeCreateModal;
      window.createAndShareChallenge = (name) => createAndShareChallenge(name);
      // Expose challenge open/close/accept helpers for static HTML bridge
      window.openChallengeModal = async (id) => {
        if (!id) return;
        try {
          setChallengeLoading(true);
          setChallengeError(null);
          const app = getApp();
          const db = getFirestore(app);
          const ref = doc(db, 'challenges', id);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            setChallengeError('Challenge not found');
            setChallengeData(null);
            setShowChallengeModal(false);
          } else {
            setChallengeData(snap.data());
            setShowChallengeModal(true);
            try { window.history.replaceState({}, '', `/challenge/${id}`); } catch (e) { /* noop */ }
          }
        } catch (err) {
          setChallengeError(String(err));
          setChallengeData(null);
          setShowChallengeModal(false);
        } finally {
          setChallengeLoading(false);
        }
      };

      window.closeChallengeModal = () => {
        try { window.history.replaceState({}, '', '/'); } catch (e) { /* noop */ }
        setShowChallengeModal(false);
      };

      window.acceptChallenge = () => {
        const cd = challengeDataRef.current;
        if (cd && cd.duration) {
          setTestDuration(Number(cd.duration));
          setTimeLeft(Number(cd.duration));
        }
        setChallengeMode(true);
        setShowChallengeModal(false);
        setOverlayHidden(true);
        setTimeout(() => inputRef.current?.focus(), 20);
      };
      // Expose exitChallenge so static shell can call into the app to exit
      try { window.exitChallenge = exitChallenge; } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }

    return () => {
      try {
        if (window.openCreateModal === openCreateModal) delete window.openCreateModal;
        if (window.closeCreateModal === closeCreateModal) delete window.closeCreateModal;
        if (window.createAndShareChallenge) delete window.createAndShareChallenge;
        if (window.openChallengeModal) delete window.openChallengeModal;
        if (window.closeChallengeModal) delete window.closeChallengeModal;
        if (window.acceptChallenge) delete window.acceptChallenge;
        if (window.exitChallenge === exitChallenge) delete window.exitChallenge;
      } catch (e) { /* noop */ }
    };
  }, [openCreateModal, closeCreateModal, createAndShareChallenge, exitChallenge]);

  // Bind static shell controls to existing business logic
  useEffect(() => {
    if (!hasStaticDOM) return;

    inputRef.current = document.getElementById('typing-input') || inputRef.current;
    typingAreaRef.current = document.getElementById('typing-area') || typingAreaRef.current;
    innerRef.current = document.getElementById('typing-inner') || innerRef.current;
    resultsRef.current = document.getElementById('results') || resultsRef.current;

    const inputEl = document.getElementById('typing-input');
    const durationEl = document.getElementById('duration-select');
    const resetEl = document.getElementById('reset-btn');
    const darkToggleEl = document.getElementById('dark-toggle');
    const tryAgainEl = document.getElementById('try-again-btn');
    const resultsChallengeOpen = document.getElementById('results-challenge-open');
    const overlayEl = document.getElementById('typing-overlay');

    // Ensure the static input is disabled when the test is completed
    if (inputEl) inputEl.disabled = isCompleted;

    const onInput = (e) => handleInputChange(e);
    const onKeyPress = (e) => handleKeyPress(e);
    const onDuration = (e) => {
      const newDuration = Number(e.target.value);
      setTestDuration(newDuration);
      setTimeLeft(newDuration);
      resetTest();
    };
    const onDark = () => setDarkMode(prev => !prev);
    const onOverlay = () => {
      setOverlayHidden(true);
      inputRef.current?.focus();
    };

    if (inputEl) {
      inputEl.addEventListener('input', onInput);
      inputEl.addEventListener('keypress', onKeyPress);
      inputEl.focus();
    }
    if (durationEl) durationEl.addEventListener('change', onDuration);
    if (resetEl) resetEl.addEventListener('click', resetTest);
    if (darkToggleEl) darkToggleEl.addEventListener('click', onDark);
    if (tryAgainEl) tryAgainEl.addEventListener('click', resetTest);
    const onResultsChallengeOpen = () => { openCreateModal(); setCreateError(null); };
    if (resultsChallengeOpen) resultsChallengeOpen.addEventListener('click', onResultsChallengeOpen);
    if (overlayEl) {
      overlayEl.addEventListener('click', onOverlay);
      // Also handle touchstart so mobile taps immediately hide the overlay
      overlayEl.addEventListener('touchstart', onOverlay, { passive: true });
    }

    // Bind Exit Challenge buttons in static shell
    const headerExitEl = document.getElementById('header-exit-challenge');
    const resultsExitEl = document.getElementById('results-exit-challenge');
    const onExit = () => {
      try { if (typeof window.exitChallenge === 'function') window.exitChallenge(); } catch (e) { /* noop */ }
      try { window.location.pathname = '/'; } catch (e) { /* noop */ }
    };
    if (headerExitEl) headerExitEl.addEventListener('click', onExit);
    if (resultsExitEl) resultsExitEl.addEventListener('click', onExit);

    return () => {
      if (inputEl) {
        inputEl.removeEventListener('input', onInput);
        inputEl.removeEventListener('keypress', onKeyPress);
      }
      if (durationEl) durationEl.removeEventListener('change', onDuration);
      if (resetEl) resetEl.removeEventListener('click', resetTest);
      if (darkToggleEl) darkToggleEl.removeEventListener('click', onDark);
      if (tryAgainEl) tryAgainEl.removeEventListener('click', resetTest);
      if (resultsChallengeOpen) resultsChallengeOpen.removeEventListener('click', onResultsChallengeOpen);
      if (overlayEl) {
        overlayEl.removeEventListener('click', onOverlay);
        overlayEl.removeEventListener('touchstart', onOverlay);
      }
      if (headerExitEl) headerExitEl.removeEventListener('click', onExit);
      if (resultsExitEl) resultsExitEl.removeEventListener('click', onExit);
    };
  }, [hasStaticDOM, handleInputChange, handleKeyPress, resetTest, isCompleted, openCreateModal]);

  // Bind and sync the static challenge modal (values and buttons)
  useEffect(() => {
    const modal = document.getElementById('challenge-modal');
    if (!modal) return;
    const backdrop = document.getElementById('challenge-backdrop');
    const closeBtn = document.getElementById('challenge-close');
    const acceptBtn = document.getElementById('challenge-accept');

    // Populate modal when data changes
    if (modal) {
      modal.classList.toggle('hidden', !showChallengeModal);
    }

    if (challengeData) {
      const w = document.getElementById('challenge-wpm'); if (w) w.textContent = String(challengeData.creatorWpm || 0);
      const a = document.getElementById('challenge-accuracy'); if (a) a.textContent = `${challengeData.creatorAccuracy || 0}%`;
      const d = document.getElementById('challenge-duration'); if (d) d.textContent = `${challengeData.duration || testDuration}s`;
      const e = document.getElementById('challenge-expires'); if (e) e.textContent = challengeData.expiresAt ? formatTimestamp(challengeData.expiresAt) : '—';
      const title = document.getElementById('challenge-title'); if (title) title.textContent = `Challenge from ${(challengeData.creatorName) || 'Unknown'}`;
    }

    const onClose = () => {
      try {
        if (typeof window.closeChallengeModal === 'function') {
          window.closeChallengeModal();
          return;
        }
      } catch (e) { /* noop */ }
      try { window.history.replaceState({}, '', '/'); } catch (e) { /* noop */ }
      setShowChallengeModal(false);
    };
    const onAccept = () => {
      if (challengeData && challengeData.duration) {
        setTestDuration(Number(challengeData.duration));
        setTimeLeft(Number(challengeData.duration));
      }
      setChallengeMode(true);
      setShowChallengeModal(false);
      setOverlayHidden(true);
      setTimeout(() => inputRef.current?.focus(), 20);
    };

    if (closeBtn) closeBtn.addEventListener('click', onClose);
    if (acceptBtn) acceptBtn.addEventListener('click', onAccept);
    if (backdrop) backdrop.addEventListener('click', onClose);

    return () => {
      if (closeBtn) closeBtn.removeEventListener('click', onClose);
      if (acceptBtn) acceptBtn.removeEventListener('click', onAccept);
      if (backdrop) backdrop.removeEventListener('click', onClose);
    };
  }, [hasStaticDOM, showChallengeModal, challengeData, challengeLoading, testDuration, challengeMode]);

  // Sync static shell visuals and text from current state
  useEffect(() => {
    if (!hasStaticDOM) return;

    const shell = document.getElementById('app-shell');
    const header = document.getElementById('app-header');
    const timer = document.getElementById('app-timer');
    const duration = document.getElementById('duration-select');
    const input = document.getElementById('typing-input');
    const inner = document.getElementById('typing-inner');
    const overlay = document.getElementById('typing-overlay');
    const results = document.getElementById('results');
    const headerExit = document.getElementById('header-exit-challenge');
    const resultsExit = document.getElementById('results-exit-challenge');
    const resultsChallengeOpen = document.getElementById('results-challenge-open');

    if (shell) {
      shell.classList.toggle('bg-gray-50', !darkMode);
      shell.classList.toggle('text-gray-900', !darkMode);
      shell.classList.toggle('bg-gray-900', darkMode);
      shell.classList.toggle('text-white', darkMode);
    }
    if (header) {
      header.classList.toggle('bg-white', !darkMode);
      header.classList.toggle('border-gray-200', !darkMode);
      header.classList.toggle('bg-gray-800', darkMode);
      header.classList.toggle('border-gray-700', darkMode);
    }
    if (timer) {
      timer.textContent = `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`;
      timer.classList.toggle('text-red-500', timeLeft <= 10 && isActive);
      timer.classList.toggle('text-blue-500', !(timeLeft <= 10 && isActive));
    }
    if (duration) {
      duration.value = String(testDuration);
      duration.disabled = isActive;
      // Hide duration picker when challengeMode is active
      try { duration.classList.toggle('hidden', !!challengeMode); } catch (e) { /* noop */ }
    }

    // Keep static input disabled when test is completed
    if (input) input.disabled = isCompleted;

    // Toggle only the top-level body class and header icon — CSS overrides handle the rest.
    try {
      const darkToggleEl = document.getElementById('dark-toggle');
      if (darkToggleEl) {
        const moon = darkToggleEl.querySelector('#dark-icon-moon');
        const sun = darkToggleEl.querySelector('#dark-icon-sun');
        if (moon) moon.classList.toggle('hidden', darkMode);
        if (sun) sun.classList.toggle('hidden', !darkMode);
      }
      try { document.body.classList.toggle('td-dark', darkMode); } catch (e) { /* noop */ }
    } catch (e) {
      // ignore if DOM not present or query fails
    }

    if (input && input.value !== userInput) {
      input.value = userInput;
    }

    if (inner) {
      const parts = [];
      let wordBuffer = '';

      for (let i = 0; i < currentText.length; i++) {
        const expected = currentText[i];
        const typed = userInput[i];
        let cls = 'relative';
        if (i < userInput.length) {
          cls += typed === expected
            ? ' text-green-600 bg-green-100'
            : ' text-red-600 bg-red-100';
        } else if (i === userInput.length) {
          cls += isActive ? ' bg-blue-500 text-white animate-pulse' : ' bg-blue-500 text-white';
        } else {
          cls += darkMode ? ' text-gray-500' : ' text-gray-400';
        }

        const cursorHere = i === userInput.length;

        if (expected === ' ') {
          // Flush accumulated characters as a single word (non-breaking)
          if (wordBuffer) {
            parts.push(`<span style="white-space:nowrap">${wordBuffer}</span>`);
            wordBuffer = '';
          }

          // If the cursor is on a space, render it as a visible span so cursor styling appears.
          if (cursorHere) {
            parts.push(`<span class="${cls}" data-cursor="true">&nbsp;</span>`);
          } else {
            // Emit a normal space character so the browser may break lines at this space
            parts.push(' ');
          }
        } else {
          // accumulate character spans for the current word
          const ch = escapeHtml(expected);
          const charSpan = `<span class="${cls}"${cursorHere ? ' data-cursor="true"' : ''}>${ch}</span>`;
          wordBuffer += charSpan;
        }
      }

      if (wordBuffer) parts.push(`<span style="white-space:nowrap">${wordBuffer}</span>`);
      inner.innerHTML = parts.join('');
    }

    if (overlay) {
      const shouldShow = !isActive && !isCompleted && userInput.length === 0 && !overlayHidden;
      overlay.classList.toggle('hidden', !shouldShow);
    }

    const wpmEl = document.getElementById('metric-wpm');
    const cpmEl = document.getElementById('metric-cpm');
    const cpsEl = document.getElementById('metric-cps');
    const accEl = document.getElementById('metric-accuracy');
    const errEl = document.getElementById('metric-errors');
    const charsEl = document.getElementById('metric-chars');
    const speedEl = document.getElementById('current-speed');
    const progressFill = document.getElementById('progress-fill');
    const progressChars = document.getElementById('progress-chars');
    const progressAcc = document.getElementById('progress-accuracy');

    if (wpmEl) wpmEl.textContent = String(metrics.wpm);
    if (cpmEl) cpmEl.textContent = String(metrics.cpm);
    if (cpsEl) cpsEl.textContent = String(metrics.cps);
    if (accEl) accEl.textContent = `${metrics.accuracy}%`;
    if (errEl) errEl.textContent = String(metrics.errors);
    if (charsEl) charsEl.textContent = String(userInput.length);
    if (speedEl) speedEl.textContent = `${metrics.wpm} WPM`;
    if (progressFill) progressFill.style.width = `${Math.min(100, (userInput.length / 500) * 100)}%`;
    if (progressChars) progressChars.textContent = `Characters typed: ${userInput.length}`;
    if (progressAcc) progressAcc.textContent = `Accuracy: ${metrics.accuracy}%`;

    if (results) {
      // Keep visibility toggling here; full DOM updates are handled once on completion
      results.classList.toggle('hidden', !isCompleted);
    }

    // Toggle Exit / Challenge buttons
    try {
      if (headerExit) headerExit.classList.toggle('hidden', !challengeMode);
      if (resultsExit) resultsExit.classList.toggle('hidden', !(isCompleted && challengeMode));
      if (resultsChallengeOpen) {
        const showResultsChallengeOpen = isCompleted && (!challengeMode || (challengeMode && challengeOutcome === 'won'));
        resultsChallengeOpen.classList.toggle('hidden', !showResultsChallengeOpen);
      }
    } catch (e) { /* noop */ }
  }, [
    hasStaticDOM,
    darkMode,
    timeLeft,
    isActive,
    testDuration,
    userInput,
    currentText,
    isCompleted,
    overlayHidden,
    metrics,
    challengeData,
    challengeMode,
    challengeOutcome,
  ]);

  if (hasStaticDOM) {
    return null;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>


      {/* Challenge Modal moved to static HTML (public/index.html) */}

      {/* Share Challenge Modal (moved to static HTML, React uses the static modal) */}


      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Privacy/Terms/Contact are now standalone pages in public/; navigation uses links */}

        {currentPage === 'home' && (
          <>
            {/* Live Metrics Bar */}


            {/* Typing Area (typewriter view) */}


            {/* Progress Indicator */}


            {/* Results Panel */}

          </>
        )}
      </main>



      {/* Footer is provided by static pages via /footer.html */}
    </div>
  );
};

export default TypingSpeedTest;